import classnames from 'classnames';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import React, { ReactElement, useState } from 'react';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { collect } from 'collect.js';
import { useTranslation } from 'react-i18next';
import BackIcon from '../../assets/logo/BackIcon';
import { CompleteBasketItem } from '../../providers/BasketProvider';
import CartIcon from '../../modules/mobile/assets/icons/CartIcon';
import { Order } from '../../services/exports/Interfaces';
import DeliveryTracker from '../delivery/DeliveryTracker';
import useTheme from '../../hooks/ui/useTheme';

interface Props {
  order: Order;
  items: CompleteBasketItem[];
  extra?: ReactElement;
  className?: string;
  hasDeliveredBy?: boolean;
}

export default function OrderBasketSummary({ order, items, extra, className, hasDeliveredBy }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Order:OrderBasketSummary' });

  const { formatCurrency } = useAppMethods();
  const theme = useTheme();

  const [open, setOpen] = useState(false);

  function renderDescription(item: CompleteBasketItem) {
    return (
      item.modifiers.length > 0 && (
        <p className="break-words text-xs text-brand-text-secondary">
          {collect(item.modifiers)
            .filter((modifier) => !!modifier)
            .implode('entry.name', ' • ')}
        </p>
      )
    );
  }

  return (
    items.length > 0 && (
      <div className={classnames('bg-mobile-notice dark:bg-black-600 p-md text-brand-text-primary', className)}>
        <Accordion
          style={{ boxShadow: 'none', backgroundColor: 'transparent' }}
          expanded={open}
          onChange={() => setOpen((current) => !current)}
        >
          <AccordionSummary
            style={{
              padding: 0,
              margin: 0,
              width: '100%',
              minHeight: 0,
            }}
            classes={{
              content: '!m-none bg-mobile-notice dark:bg-black-600',
            }}
          >
            <div className="w-full flex justify-between">
              <button className=" flex items-center space-x-md">
                <CartIcon className="stroke-brand-icon-color h-4" />
                <span className="text-sm font-semibold">{t(open ? 'labels.hide' : 'labels.show_order_summary')}</span>
                <BackIcon
                  className={classnames('!w-2 transition delay-200', {
                    '-rotate-90': !open,
                    'rotate-90': open,
                  })}
                  color={theme.color.brand.icon.color}
                />
              </button>
              <p className="my-auto text-sm font-semibold">{formatCurrency(order.total)}</p>
            </div>
          </AccordionSummary>
          <AccordionDetails
            style={{ padding: 0, marginTop: 0 }}
            className="text-brand-text-primary bg-mobile-notice dark:bg-black-600 space-y-md !pt-md"
          >
            {items.map((item, index) => (
              <article key={`basket-item-${index}`}>
                <div className="flex justify-between">
                  <p className="text-xs font-medium">
                    {item.quantity}x {item.entry.name}
                  </p>
                  <p className="text-xs font-medium">{formatCurrency(item.actual_price)}</p>
                </div>
                {renderDescription(item)}
              </article>
            ))}
            {extra}
            {hasDeliveredBy && <DeliveryTracker.DeliveredBy order={order} className="text-brand-text-secondary" />}
          </AccordionDetails>
        </Accordion>
      </div>
    )
  );
}
